import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useUsers } from '@/lib/useUsers';
import { useOrganizationHierarchy } from '@/lib/useOrganizationHierarchy';
import { useToast } from '@/lib/toast';
import { supabase } from '@/lib/supabase';
import { User, Mail, Shield, Key, Bell, ShieldCheck, Building, Check, Loader2 } from 'lucide-react';
import { ChangePasswordModal } from '@/components/auth/ChangePasswordModal';
import type { UserView } from './UserSidebar';

export function UserProfile({ onNavigate }: { onNavigate?: (v: UserView) => void }) {
  const { session } = useAuth();
  const user = session?.user;
  const { profiles } = useUsers();
  const { organizations, departments } = useOrganizationHierarchy();
  const toast = useToast();

  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const profile = profiles.find(p => p.id === user?.id);
  const org = organizations.find(o => o.id === profile?.organization_id)?.organization_name || 'Organization Not Set';
  const dept = departments.find(d => d.id === profile?.department)?.name || profile?.department || 'Department Not Set';

  const [editName, setEditName] = useState(user?.name || '');
  const [editDept, setEditDept] = useState(profile?.department || '');

  const handleStartEdit = () => {
    setEditName(user?.name || '');
    setEditDept(profile?.department || '');
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    if (!user?.id || !supabase) return;
    setSavingProfile(true);
    try {
      const sessionRes = await supabase.auth.getSession();
      const token = sessionRes?.data?.session?.access_token || session?.access_token;
      if (!token) throw new Error('Your session has expired. Please sign in again.');

      const res = await fetch('/api/update-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fullName: editName,
          department: editDept || null,
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      toast.push('Profile updated successfully!');
      setIsEditing(false);
      window.dispatchEvent(new Event('compass_auth_changed'));
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      toast.push(err.message || 'Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-foreground">My Profile</h1>
        <p className="text-muted-foreground mt-1">Manage your personal information and preferences.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-1 space-y-6">
          <div className="neo rounded-[2rem] p-6 text-center flex flex-col items-center overflow-hidden">
            <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-inner shrink-0">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </div>
            <h2 className="text-xl font-bold text-foreground truncate max-w-full px-2" title={user?.name || 'User'}>
              {user?.name || 'User'}
            </h2>
            <p className="text-muted-foreground text-xs font-mono truncate max-w-full px-2 mt-1 mb-3" title={user?.email || ''}>
              {user?.email || 'No email'}
            </p>
            <div className="mb-4 max-w-full truncate">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold max-w-full truncate">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{user?.role || 'Standard User'}</span>
              </span>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              Active Account
            </span>
          </div>

          <div className="neo rounded-[2rem] p-6">
            <h3 className="font-semibold text-foreground mb-4">Quick Settings</h3>
            <div className="space-y-4">
              <button 
                type="button"
                onClick={() => onNavigate?.('notifications')}
                className="flex items-center gap-3 w-full text-left text-sm text-muted-foreground hover:text-foreground transition group"
              >
                <Bell className="h-4 w-4 group-hover:text-primary transition-colors shrink-0" /> Notifications
              </button>
              <button 
                type="button"
                onClick={() => setIsChangePasswordOpen(true)}
                className="flex items-center gap-3 w-full text-left text-sm text-muted-foreground hover:text-foreground transition group"
              >
                <Key className="h-4 w-4 group-hover:text-primary transition-colors shrink-0" /> Change Password
              </button>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-6">
          <div className="neo rounded-[2rem] p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6 border-b border-border pb-4">Personal Information</h3>
            <div className="space-y-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded-xl border border-input bg-background/50 px-4 py-2.5 text-sm font-medium outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 neo-inset"
                  />
                ) : (
                  <div className="flex items-center gap-3 neo-inset rounded-xl px-4 py-3 border-none bg-transparent min-w-0">
                    <User className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate" title={user?.name || 'Not provided'}>
                      {user?.name || 'Not provided'}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Email Address</label>
                <div className="flex items-center gap-3 neo-inset rounded-xl px-4 py-3 border-none bg-transparent opacity-80 min-w-0" title={user?.email || 'Not provided'}>
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium truncate font-mono">
                    {user?.email || 'Not provided'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Role / Title</label>
                <div className="flex items-center gap-3 neo-inset rounded-xl px-4 py-3 border-none bg-transparent opacity-80 min-w-0" title="Role is assigned by administrator">
                  <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {user?.role || 'Standard User'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Organization / Department</label>
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 neo-inset rounded-xl px-4 py-2 border-none bg-transparent text-xs text-muted-foreground min-w-0">
                      <Building className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{org}</span>
                    </div>
                    <select
                      value={editDept}
                      onChange={(e) => setEditDept(e.target.value)}
                      className="w-full rounded-xl border border-input bg-background/50 px-3 py-2 text-sm font-medium outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 neo-inset"
                    >
                      <option value="">Select Department...</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 neo-inset rounded-xl px-4 py-3 border-none bg-transparent min-w-0">
                    <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate" title={`${org} ${profile?.department ? `/ ${dept}` : ''}`}>
                      {org} {profile?.department ? `/ ${dept}` : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-8 flex justify-end gap-3">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    disabled={savingProfile}
                    onClick={() => setIsEditing(false)}
                    className="rounded-xl px-5 py-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={savingProfile || !editName.trim()}
                    onClick={handleSaveProfile}
                    className="inline-flex items-center gap-2 neo-interactive bg-[var(--color-primary)] text-[var(--color-primary-content)] px-6 py-2.5 rounded-[1rem] text-sm font-bold hover:brightness-110 transition-all disabled:opacity-50"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button 
                  type="button"
                  onClick={handleStartEdit}
                  className="neo-interactive bg-[var(--color-primary)] text-[var(--color-primary-content)] px-6 py-2.5 rounded-[1rem] text-sm font-bold hover:brightness-110 transition-all"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        open={isChangePasswordOpen} 
        onClose={() => setIsChangePasswordOpen(false)} 
      />
    </div>
  );
}
